// Pure aggregator: un giorno dello storico del Diario (risultati + voci libere)
// pronto per il render. Niente import React, niente side effects.

// Palette deterministica colori programma. Le classi sono statiche per non rompere il
// purge di Tailwind. Indici via hash stabile del program_id.
const PROGRAM_COLORS = [
	{
		key: "volt",
		text: "text-volt",
		bgTint: "bg-volt/10",
		bgStrong: "bg-volt/20",
		border: "border-volt/30",
		borderStrong: "border-volt/40",
		dot: "bg-volt",
		ring: "ring-volt/40",
	},
	{
		key: "sky",
		text: "text-sky-400",
		bgTint: "bg-sky-400/10",
		bgStrong: "bg-sky-400/20",
		border: "border-sky-400/30",
		borderStrong: "border-sky-400/40",
		dot: "bg-sky-400",
		ring: "ring-sky-400/40",
	},
	{
		key: "amber",
		text: "text-amber-400",
		bgTint: "bg-amber-400/10",
		bgStrong: "bg-amber-400/20",
		border: "border-amber-400/30",
		borderStrong: "border-amber-400/40",
		dot: "bg-amber-400",
		ring: "ring-amber-400/40",
	},
	{
		key: "fuchsia",
		text: "text-fuchsia-400",
		bgTint: "bg-fuchsia-400/10",
		bgStrong: "bg-fuchsia-400/20",
		border: "border-fuchsia-400/30",
		borderStrong: "border-fuchsia-400/40",
		dot: "bg-fuchsia-400",
		ring: "ring-fuchsia-400/40",
	},
	{
		key: "indigo",
		text: "text-indigo-400",
		bgTint: "bg-indigo-400/10",
		bgStrong: "bg-indigo-400/20",
		border: "border-indigo-400/30",
		borderStrong: "border-indigo-400/40",
		dot: "bg-indigo-400",
		ring: "ring-indigo-400/40",
	},
];

function hashString(s) {
	let h = 0;
	const str = String(s || "");
	for (let i = 0; i < str.length; i++) {
		h = (h << 5) - h + str.charCodeAt(i);
		h |= 0;
	}
	return Math.abs(h);
}

/**
 * Restituisce un oggetto colore deterministico in base al program_id.
 * Lo stesso programma ottiene sempre lo stesso colore tra sessioni.
 */
export function getProgramColor(programId) {
	const idx = hashString(programId) % PROGRAM_COLORS.length;
	return PROGRAM_COLORS[idx];
}

export const PROGRAM_COLOR_PALETTE = PROGRAM_COLORS;

const isManualResult = (result) => !result?.program_id || !result?.day_id;

/**
 * L'«esercizio» che la riga del Diario rende per un risultato.
 *
 * Con la prescrizione agganciata dal server (`result.prescription.exercise`)
 * il dettaglio confronta previsto ed eseguito; senza — risultato manuale, o
 * esercizio tolto dal programma dopo l'allenamento — la riga nasce dal
 * risultato stesso, e per un conditioning dal template salvato con lui.
 */
export function exerciseForResult(result) {
	const prescribed = result?.prescription?.exercise;
	const base =
		prescribed && typeof prescribed === "object"
			? prescribed
			: {
					...(result?.conditioning_config || {}),
					exercise_id: result?.exercise_id,
					exercise_name: result?.exercise_name,
					exercise_type: result?.exercise_type || "strength",
					conditioning_type:
						result?.conditioning_type ||
						result?.conditioning_config?.conditioning_type ||
						null,
				};
	return {
		...base,
		exercise_name: base.exercise_name || result?.exercise_name,
		exercise_type: base.exercise_type || result?.exercise_type || "strength",
		result,
		_uid: result?.result_id,
		// Ogni risultato dell'atleta si elimina dal Diario, anche quelli di
		// programma: se qualcosa non torna, lo toglie da qui.
		canDelete: true,
		// Si corregge con lo stesso modulo con cui è stato inserito: quello
		// della Dashboard per la riga del programma, quello manuale per i
		// manuali. Senza riga (esercizio tolto dal programma) quel modulo non
		// si può ricostruire, e il risultato si può solo eliminare.
		canEdit:
			Boolean(result?.result_id) &&
			(isManualResult(result) || Boolean(prescribed)),
	};
}

/**
 * Lo storico con un risultato corretto al suo posto. La risposta della PUT è
 * il documento nudo: prescrizione, etichette della giornata e «visto dal
 * coach» li aggiunge lo storico, quindi si tengono dal vecchio. Il resto viene
 * solo dalla risposta: un campo svuotato il server lo toglie, e fondere i due
 * documenti lo farebbe ricomparire.
 */
export function replaceHistoryResult(days, updated) {
	if (!updated?.result_id) return days;
	return days.map((day) => ({
		...day,
		results: day.results.map((result) =>
			result.result_id === updated.result_id
				? {
						...updated,
						prescription: result.prescription,
						program_day: result.program_day,
						coach_feedback_seen: result.coach_feedback_seen,
						coach_feedback_seen_at: result.coach_feedback_seen_at,
					}
				: result,
		),
	}));
}

/** Lo storico senza un risultato; un giorno rimasto vuoto sparisce. */
export function removeHistoryResult(days, resultId) {
	return days
		.map((day) => ({
			...day,
			results: day.results.filter((result) => result.result_id !== resultId),
		}))
		.filter((day) => day.results.length > 0 || day.entries.length > 0);
}

/**
 * Un giorno dello storico: i risultati raggruppati per giornata di programma
 * (nell'ordine in cui sono stati registrati), poi quelli inseriti a mano.
 * Nessun esercizio prescritto e non eseguito: il Diario registra il fatto.
 *
 * Output: { date, groups: [{ key, programId, dayId, programTitle, dayTitle,
 *   isManual, color, items: [exercise] }], entries }
 */
export function buildHistoryDay({ date, results, entries }) {
	const groups = [];
	const byKey = new Map();
	let manual = null;

	(results || []).forEach((result) => {
		if (!result) return;
		if (isManualResult(result)) {
			if (!manual) {
				manual = {
					key: "manual",
					programId: null,
					dayId: null,
					programTitle: "",
					dayTitle: "",
					isManual: true,
					color: null,
					items: [],
				};
			}
			manual.items.push(exerciseForResult(result));
			return;
		}
		const key = `${result.program_id}::${result.day_id}`;
		let group = byKey.get(key);
		if (!group) {
			const labels = result.program_day || result.prescription || {};
			group = {
				key,
				programId: result.program_id,
				dayId: result.day_id,
				programTitle: labels.program_name || "",
				dayTitle: labels.day_name || "",
				isManual: false,
				color: getProgramColor(result.program_id),
				items: [],
			};
			byKey.set(key, group);
			groups.push(group);
		}
		group.items.push(exerciseForResult(result));
	});

	if (manual) groups.push(manual);
	return { date, groups, entries: entries || [] };
}
